import Modal from "components/DesignSystem/Modal/Modal";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import {
  useGetIndividualPerkQuery,
  useRedeemDealMutation,
} from "store/apis/rewards";
import Async from "components/DesignSystem/AsyncComponents/Async";
import {
  Children,
  isValidElement,
  ReactNode,
  useContext,
  useEffect,
} from "react";
import { HowIcon } from "components/icons/HowIcon";
import { Copycode } from "./Copycode";
import {
  SPECIAL_EMAIL,
  GENERIC,
  INTRO_EMAIL,
  PROMO_CODE,
  SPECIAL_URL,
} from "constants/perks";
import { useToast } from "hooks/useToast";
import { debounce } from "utils/debouncing";
import { Button } from "components/DesignSystem/Button/Button";
import { ArrowUpRight12 } from "components/icons/ArrowUpRight12";
import authContext from "jwt_context&axios/authContext";
import { useDispatch } from "react-redux";
import { setRedirectPath } from "store/slices/subscriptionModal";
import { useAnalytics } from "hooks/useAnalytics";
import { RedeemConfirmationModal } from "./RedeemConfirmationModal";
import { useModal } from "hooks/useModal";
import Placeholder from "static/images/Imageplaceholder.svg";
import { RedeemInstructions } from "./RedeemInstructions";
import { PERK_OPENED, PERK_REDEEMED } from "constants/analyticsEvents";
import { useHistory, useParams } from "react-router-dom";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import PerksBannerBackground from "static/images/PerksBannerBackground.svg";
import { Info } from "components/icons/Info";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

const PerkMarkdownInstructions = ({
  instructions,
}: {
  instructions: string;
}) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      ol({ children, start }) {
        return (
          <ol
            start={start}
            className="t-list-decimal t-pl-4 t-text-body-sm t-text-text-30"
          >
            {children}
          </ol>
        );
      },
      ul({ children }) {
        return (
          <ul className="t-list-decimal t-pl-4 t-text-body-sm t-text-text-30">
            {children}
          </ul>
        );
      },
      p({ children }) {
        return (
          <p className="t-text-text-30 t-text-body-sm t-w-full">{children}</p>
        );
      },
      a({ children, href }) {
        return (
          <a href={href} className="t-text-body-sm t-text-purple">
            {children}
          </a>
        );
      },
    }}
  >
    {instructions}
  </ReactMarkdown>
);

export type PerksNewModalProps = {
  parentUrl: string;
};

const PerkIntrusctions = ({ instructions }: { instructions: ReactNode[] }) => {
  return (
    <div className="t-flex t-flex-col t-gap-2.5">
      {instructions?.map((instruction, index) => (
        <div key={index} className="t-flex t-gap-3">
          <div>
            <div className="t-text-caption t-py-0.5 t-px-2 t-rounded-2xl t-bg-purple-0 t-flex t-items-center t-justify-center t-text-purple t-w-[1.4375rem]">
              {index + 1}
            </div>
          </div>
          <div className="t-text-body-sm t-text-text-30">{instruction}</div>
        </div>
      ))}
    </div>
  );
};

export const PerksNewModal = ({ parentUrl }: PerksNewModalProps) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const { perkSlug } = useParams<{ perkSlug: string }>();

  const {
    data: perk,
    isSuccess,
    isLoading,
    isError,
  } = useGetIndividualPerkQuery(
    { groupId: groupId, perkSlug: perkSlug },
    { skip: !perkSlug || !groupId }
  );
  const {
    heading,
    website,
    name,
    description,
    perk_type,
    redeem_details,
    perk_id,
    is_redeemed,
    instructions,
  } = perk || {};
  const [redeemDeal, { isLoading: isRedeemLoading }] = useRedeemDealMutation();
  const { authtoken } = useContext(authContext);
  const { alertToast, successToast } = useToast();
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const confirmRedeemModal = useModal();
  const history = useHistory();
  const { isCustomer } = useRoleBasedView();
  const { update } = useUpdateQuery();
  const queryParam = useQuery();
  const currentSort = queryParam.get("sort") || "";

  const isPromoCode = perk_type === PROMO_CODE;
  const isIntoEmail = perk_type === INTRO_EMAIL;
  const isGeneric = perk_type === GENERIC;
  const isSpecialEmail = perk_type === SPECIAL_EMAIL;
  const isSpecialUrl = perk_type === SPECIAL_URL;

  const promoCodeMessage = "Promo code copied to clipboard";
  const emailCopiedMessage = "Email copied to clipboard";

  const copyMessage = isPromoCode
    ? promoCodeMessage
    : isSpecialEmail
    ? emailCopiedMessage
    : "";

  const promocode = isPromoCode
    ? redeem_details?.at(0)?.promo_code
    : isSpecialEmail
    ? redeem_details?.at(0)?.special_email
    : "";

  const promocodeLabel = isPromoCode ? "Code" : isSpecialEmail ? "Email" : "";

  const onCopy = debounce(() => {
    if (promocode) {
      navigator.clipboard.writeText(promocode);
      successToast({
        message: copyMessage,
      });
    }
  }, 300);

  const showFooter =
    !isGeneric && !isSpecialEmail && !(isPromoCode && is_redeemed);

  const redirect = (link?: string) => {
    if (isSpecialUrl) {
      const url = perk?.redeem_details?.at(0)?.url || link;
      if (url) {
        window.open(url, "_blank");
      }
    }
  };

  const onclose = () => {
    const basePath = isCustomer ? parentUrl : `/crm/${groupId}/perks`;
    history.push(`${basePath}?${queryParam.toString()}`);
  };
  const genericInstruction = perk?.redeem_details?.[0]?.redemption_instructions;
  const specialEmailInstructions =
    perk?.redeem_details?.[0]?.redeem_instructions;
  const showCopyCode =
    is_redeemed && promocode && (isPromoCode || isSpecialEmail);

  const redeemPerk = async () => {
    try {
      const res = await redeemDeal({
        perkId: perk_id || "",
        groupId,
        perkType: perk_type || "",
      }).unwrap();
      if (isIntoEmail) {
        confirmRedeemModal.close();
      }

      redirect(res?.at(0)?.url || "");

      trackEvent(PERK_REDEEMED, {
        email: authtoken?.email || "",
        group_id: groupId,
        perk_type: perk_type || "",
        perk_id: perk_id || "",
        perk_name: name || "",
      });
    } catch (e) {
      alertToast({ message: "Something went wrong!" });
      dispatch(setRedirectPath("/perks"));
    }
  };

  const introEmailDetails = [
    "Click on redeem now",
    "This will ask for redemption confirmation for perk",
    "Proceeding with that will fire email to Inkle.",
  ];

  const perkUrl = perk?.redeem_details
    ? perk?.redeem_details?.[0]?.promo_code_redeem_url
    : "";

  const getFooter = () => {
    switch (perk_type) {
      case PROMO_CODE:
        return (
          <div className="t-flex t-w-full t-justify-end t-items-center t-gap-3">
            <Button onClick={onclose}>Cancel</Button>
            <Button
              isLoading={isRedeemLoading}
              onClick={redeemPerk}
              customType="primary"
            >
              Reveal Code
            </Button>
          </div>
        );
      case INTRO_EMAIL:
        return (
          <div className="t-flex t-w-full t-justify-end t-items-center t-gap-3">
            <Button onClick={onclose}>Cancel</Button>
            <Button
              onClick={
                !is_redeemed ? () => confirmRedeemModal.open() : undefined
              }
              customType="primary"
            >
              <div className="t-flex t-gap-1.5 t-items-center">
                <span>{is_redeemed ? "Email sent" : "Send perk mail"}</span>
                <ArrowUpRight12 />
              </div>
            </Button>
          </div>
        );
      case SPECIAL_URL:
        return (
          <div className="t-flex t-w-full t-justify-end t-items-center t-gap-3">
            <Button onClick={onclose}>Cancel</Button>
            <Button
              isLoading={isRedeemLoading}
              onClick={is_redeemed ? () => redirect() : () => redeemPerk()}
              customType="primary"
            >
              <div className="t-flex t-gap-1.5 t-items-center">
                <span>Visit Link</span>
                <ArrowUpRight12 />
              </div>
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (isSuccess) {
      trackEvent(PERK_OPENED, {
        perk_type: perk_type || "",
        perk_id: perk_id || "",
        perk_name: name || "",
      });
    }
  }, [isSuccess]);

  return (
    <Modal.Root open onOpenChange={onclose}>
      <Modal.Content size="regular">
        <Async.Root
          isLoading={isLoading}
          isError={isError}
          isSuccess={isSuccess}
          isEmpty={!perk || !name}
        >
          <Async.Success>
            <Modal.Body>
              <section className="t-flex t-flex-col t-gap-4">
                <div
                  style={{ backgroundImage: `url(${PerksBannerBackground})` }}
                  className="t-w-[31rem] t-h-20 t-bg-cover t-bg-center t-rounded t-flex t-justify-end t-px-5 sm:t-w-full"
                ></div>
                <div
                  className="t-mx-[1.125rem] t-h-[4.375rem] t-w-[4.375rem] t-p-4 t-rounded-full  t-bg-white t-shrink -t-mt-14 t-shadow t-flex t-justify-center t-items-center"
                  style={{
                    backgroundColor: perk?.icon_bg_color,
                  }}
                >
                  {perk?.icon ? (
                    <img
                      src={perk?.icon}
                      alt="Perk Icon"
                      className="t-w-8 t-h-8 t-rounded-sm t-object-contain"
                    />
                  ) : (
                    <img src={Placeholder} alt={name} className="t-w-8 t-h-8" />
                  )}
                </div>
                <div className="t-px-5 t-flex t-flex-col t-gap-6">
                  <div className="t-text-subtitle t-text-text-60">
                    <div>{name}</div>
                    <a className="t-text-body-sm t-text-purple" href={website}>
                      {website}
                    </a>
                  </div>
                  <div className="t-text-subtext t-text-text-100">
                    {heading}
                  </div>
                  {description && (
                    <div className="t-flex t-flex-col t-gap-1">
                      <div className="t-flex t-gap-1 t-items-center t-text-button-sm t-text-text-60">
                        <Info size="12" stroke="1.2" />
                        <div>Details</div>
                      </div>
                      <RedeemInstructions instructions={description || ""} />
                    </div>
                  )}
                  <div className="t-flex t-flex-col t-gap-2">
                    <div className="t-text-button-sm t-text-text-60 t-flex t-items-center t-gap-1">
                      <HowIcon size="12" />
                      <div>How to redeem</div>
                    </div>
                    {instructions && (
                      <PerkMarkdownInstructions instructions={instructions} />
                    )}
                  </div>
                  {showCopyCode && (
                    <Copycode
                      label={promocodeLabel}
                      value={promocode || ""}
                      onCopy={onCopy}
                    />
                  )}
                </div>
                {confirmRedeemModal.isOpen && (
                  <RedeemConfirmationModal
                    open={confirmRedeemModal.isOpen}
                    onClose={confirmRedeemModal.close}
                    onSend={redeemPerk}
                    isLoading={isRedeemLoading}
                    name={name}
                  />
                )}
              </section>
            </Modal.Body>
            {showFooter && <Modal.Footer>{getFooter()}</Modal.Footer>}
          </Async.Success>
        </Async.Root>
      </Modal.Content>
    </Modal.Root>
  );
};
